import {
    parseQuery,
    requestInterval,
    storageRepository,
    uaParser,
    visibility,
} from '@betgames/bg-tools';
import { ClassicGameDefinition } from '@bg-shared/utils/ClassicGameDefinition';
import {
    IAuthResponse,
    IAuthUrlParams,
    IReverseAuthResponse,
} from '@bg-shared/business/Auth/interfaces';
import { httpClientRepository } from '@bg-shared/infrastructure/HttpClient';
import { favoriteOptionsEntity } from '@bg-shared/business/FavoriteOptions';
import { sessionStore } from '@bg-shared/business/Session';
import { balanceStore } from '@bg-shared/business/Balance';
import { initialRepository } from '@bg-shared/infrastructure/Initial';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { API_URLS } from '@bg-shared/constants/apiUrls';
import { GameId } from '@bg-shared/enums/GameId';
import { analyticsApi } from '@bg-shared/business/Analytics';
import { playerSettingsApi, playerSettingsStore } from '@bg-shared/business/PlayerSettings';
import { webAPIIntegration } from '@bg-shared/integrations/WebIntegration/webAPIIntegration';
import {
    WebIntegrationErrorCodes,
    WebIntegrationType,
} from '@bg-shared/integrations/WebIntegration/enums';
import { IBGIWebIntegration } from '@bg-shared/integrations/WebIntegration/interfaces';
import { t } from '@bg-shared/business/Translate';
import { authStore } from './Auth.store';
import { authConfig } from './Auth.config';
import { authValidator } from './Auth.validator';
import { AuthType } from './enums';

export default class AuthService {
    private logoutWebAPI(): void {
        webAPIIntegration<IBGIWebIntegration>(WebIntegrationType.BGI, (api) => {
            api.balanceUpdate('0');
            api.errorMessage(t.string('please_login'), WebIntegrationErrorCodes.BGI_INVALID_AUTH);
        });
    }

    private handleSuccess(
        response: Partial<IAuthResponse | IReverseAuthResponse>,
        language: string,
    ): void {
        authConfig.setHeaders(response.auth, language);
        authConfig.setPlayerID(response.playerId);

        authStore.update(true);
        Object.entries(response.favoriteBettingOptionsIds).forEach(([gameId, optionsIds]) => {
            favoriteOptionsEntity.update(Number(gameId), optionsIds);
        });

        balanceStore.updateBalanceStore({ balance: response?.balance });
    }

    private configurePartnerSettings(): Promise<void> {
        return initialRepository
            .fetch(sessionStore.partnerCode, { transformResponse: false })
            .then((response) => {
                if (response.enabledGames.length === 1) {
                    sessionStore.update({ singleGame: response.enabledGames[0].gameId });
                }

                if (sessionStore.session.singleGame) {
                    response.isCombinationEnabled = false;
                    response.enabledGames = [
                        response.enabledGames.find(
                            (game) => game.gameId === sessionStore.session.singleGame,
                        ),
                    ];
                }

                response.enabledGames = response.enabledGames.filter((game) =>
                    ClassicGameDefinition.isClassic(game.gameId),
                );

                partnerSettingsStore.update(
                    {
                        ...response,
                        partnerCurrency: response.currency,
                    },
                    sessionStore.language,
                );

                playerSettingsStore.casinoMode =
                    (response.isCasinoViewEnabled && response.isDefaultCasinoMode) ||
                    partnerSettingsStore.isLithuaniaJurisdiction;
            });
    }

    public authorise(query: string): Promise<void> {
        authConfig.setConfig(AuthType.DEFAULT, query);

        const params = parseQuery<IAuthUrlParams>(query);

        const authParams = authValidator.validateQueryParams(params);

        httpClientRepository.configureApiUrl(params.apiUrl);

        return httpClientRepository
            .post<IAuthResponse>(API_URLS.AUTH, {
                transformRequest: true,
                transformResponse: false,
                data: {
                    isMobile: uaParser.isMobile,
                    partnerCode: authParams.partnerCode,
                    token: authParams.partnerToken,
                    language: authParams.language,
                    oddsFormat: authParams.oddsFormat,
                    sid: authParams.sid,
                },
            })
            .catch(
                (error) =>
                    ({
                        auth: null,
                        webSocketsToken: error?.webSocketsToken,
                        skywardDeluxeToken: error?.skywardDeluxeToken,
                    }) as Partial<IAuthResponse>,
            )
            .then((response) => {
                if (response.auth) {
                    this.handleSuccess(response, authParams.language);
                }

                sessionStore.configure(response, authParams);

                const storageKey = 'forcedVideoOnce3';
                const forcedVideoOnce = storageRepository.getItem(storageKey);

                if (response.settings) {
                    const responseSettings = { ...response.settings };

                    if (!forcedVideoOnce) {
                        responseSettings.gui = {
                            enabled: false,
                            tipSeen: false,
                            forcedOnLowQuality: false,
                        };
                        storageRepository.setItem(storageKey, '1');
                    }

                    playerSettingsStore.update({
                        ...playerSettingsStore.value,
                        ...responseSettings,
                    });

                    if (!forcedVideoOnce) {
                        playerSettingsApi.update(() => {});
                    }
                }

                playerSettingsStore.resolveGuiMode(authParams.onlyGUI, authParams.defaultGUI);

                analyticsApi.enableTracking({
                    partnerCode: authParams.partnerCode,
                    userID: response.playerId,
                    locale: authParams.language,
                    isNewIntegration: authParams.newIntegration,
                    scriptIntegration: authParams.scriptIntegration,
                    onlyGUI: authParams.onlyGUI,
                    defaultGUI: authParams.defaultGUI,
                    defaultGame: authParams.defaultGame,
                });

                return response;
            })
            .then(() => this.configurePartnerSettings());
    }

    public reverseAuthorise(query: string): Promise<void> {
        authConfig.setConfig(AuthType.REVERSE, query);

        return httpClientRepository
            .get<IReverseAuthResponse>(`${API_URLS.AUTH_REVERSE}${query}`)
            .catch((error) => {
                if (!error?.partnerCode) {
                    throw new Error(
                        'License is not found (partner code is not available), please contact support',
                    );
                }

                return {
                    auth: null,
                    webSocketsToken: error?.webSocketsToken,
                    partnerCode: error?.partnerCode,
                    language: error?.language,
                    homeUrl: error?.homeUrl,
                    gameId: error?.gameId,
                } as Partial<IReverseAuthResponse>;
            })
            .then((response) => {
                if (response.auth) {
                    this.handleSuccess(response, response.language);
                }

                sessionStore.configure(response);
                analyticsApi.enableTracking({
                    partnerCode: response.partnerCode,
                    userID: response.playerId,
                    locale: response.language,
                });

                return response;
            })
            .then(() => this.configurePartnerSettings());
    }

    public validate(force = false): Promise<void> {
        return new Promise((resolve, reject) => {
            const isDefault = authConfig.authType === AuthType.DEFAULT;

            if (!authStore.value || force) {
                if (!authConfig.authQuery) {
                    this.logoutWebAPI();

                    reject(new Error('Session expired, please re-login'));

                    webAPIIntegration(WebIntegrationType.BGI, (api) => {
                        api.errorMessage(
                            'Your session has ended',
                            WebIntegrationErrorCodes.INVALID_TOKEN,
                        );
                    });
                }

                const authMethod = (isDefault ? this.authorise : this.reverseAuthorise).bind(this);
                authMethod(authConfig.authQuery).then(resolve).catch(reject);
            } else {
                resolve();
            }
        });
    }

    public reinitializeGameSession(gameId: GameId): void {
        httpClientRepository.get(`${API_URLS.RE_INITI_SESSION}/${gameId}`).catch((error) => {
            console.error('Issue with re-init session: ', error);
        });
    }

    public refreshTokenEvent(frequency: number): void {
        let cancelInterval: () => void;

        const ping = (): void => {
            httpClientRepository
                .get<boolean>(API_URLS.REFRESH_TOKEN)
                .then((isValid) => {
                    if (!isValid) {
                        if (cancelInterval) {
                            cancelInterval();
                        }

                        authConfig.clearHeaders();
                        authStore.update(false);
                        sessionStore.clearToken();
                        this.logoutWebAPI();
                    }
                })
                .catch((e) => {
                    console.warn('Refresh token error', e);
                    webAPIIntegration(WebIntegrationType.BGI, (api) => {
                        api.errorMessage(
                            'Your session has ended',
                            WebIntegrationErrorCodes.INVALID_TOKEN,
                        );
                    });
                });
        };

        cancelInterval = requestInterval(ping, frequency);

        visibility.setEvent((hidden) => {
            if (!hidden) {
                ping();
            }
        });
    }
}

export const authService = new AuthService();
